import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuicideIntentAssessment } from "../../../store/slices/suicideIntentSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function BeckSuicideFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentSuicideIntentAssessment, isLoading, error } = useSelector(
        (state) => state.suicideIntent || {}
    );

    useEffect(() => {
        if (id) dispatch(fetchSuicideIntentAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentSuicideIntentAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentSuicideIntentAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading SIS Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentSuicideIntentAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentSuicideIntentAssessment;

    const sisItems = [
        { title: "Isolation", scoreKey: "isolation_score", valueKey: "isolation_value" },
        { title: "Timing", scoreKey: "timing_score", valueKey: "timing_value" },
        { title: "Precautions against discovery", scoreKey: "precautions_score", valueKey: "precautions_value" },
        { title: "Acting to Get Help", scoreKey: "acting_to_get_help_score", valueKey: "acting_to_get_help_value" },
        { title: "Final Acts (Anticipation of Death)", scoreKey: "final_acts_score", valueKey: "final_acts_value" },
        { title: "Active Preparation for Attempt", scoreKey: "active_preparation_score", valueKey: "active_preparation_value" },
        { title: "Suicide Note", scoreKey: "suicide_note_score", valueKey: "suicide_note_value" },
        { title: "Overt Communication Before Attempt", scoreKey: "overt_communication_score", valueKey: "overt_communication_value" },
        { title: "Alleged Purpose of Attempt", scoreKey: "alleged_purpose_score", valueKey: "alleged_purpose_value" },
        { title: "Expectations of Fatality", scoreKey: "expectations_fatality_score", valueKey: "expectations_fatality_value" },
        { title: "Conception of Method's Lethality", scoreKey: "conception_lethality_score", valueKey: "conception_lethality_value" },
        { title: "Seriousness of Attempt", scoreKey: "seriousness_score", valueKey: "seriousness_value" },
        { title: "Attitude Toward Living/Dying", scoreKey: "attitude_living_dying_score", valueKey: "attitude_living_dying_value" },
        { title: "Conception of Medical Rescuability", scoreKey: "conception_rescuability_score", valueKey: "conception_rescuability_value" },
        { title: "Degree of Premeditation", scoreKey: "premeditation_score", valueKey: "premeditation_score" },
        { title: "Reaction to Attempt", scoreKey: "reaction_attempt_score", valueKey: "reaction_attempt_value" },
        { title: "Visualization of Death", scoreKey: "visualization_death_score", valueKey: "visualization_death_value" },
        { title: "Number of Previous Attempts", scoreKey: "previous_attempts_score", valueKey: "previous_attempts_value" },
        { title: "Alcohol Intake Relationship", scoreKey: "alcohol_intake_alcohol_score", valueKey: "alcohol_intake_alcohol_value" },
        { title: "Drug Intake Relationship", scoreKey: "intake_drug_score", valueKey: "intake_drug_value" }
    ];

    const calculateTotal = () => {
        let total = 0;
        sisItems.forEach(item => {
            total += parseInt(d[item.scoreKey] || 0);
        });
        return total;
    };

    const totalScore = calculateTotal();

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 10px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 25px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 10px !important; left: 10px !important; }
                .logo img { height: 55px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 15px !important; border-bottom: 2px solid #000 !important; padding-bottom: 5px !important; }
                .header h3 { font-size: 1.4rem !important; font-weight: bold !important; }
                .header h6 { font-size: 0.9rem !important; text-transform: uppercase !important; margin-top: 2px !important; }

                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 10px !important; font-size: 10px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 4px 6px !important; }
                .label-bg { background: #eee !important; font-weight: bold !important; width: 18% !important; }

                .sis-table { width: 100% !important; border-collapse: collapse !important; font-size: 9.5px !important; }
                .sis-table th, .sis-table td { border: 1px solid #000 !important; padding: 4px 6px !important; }
                .sis-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }

                .result-summary { margin-top: 15px !important; border: 2px solid #000 !important; display: flex !important; page-break-inside: avoid !important; }
                .result-score { background: #000 !important; color: #fff !important; padding: 10px 20px !important; text-align: center !important; min-width: 120px !important; }
                .result-score h2 { font-size: 1.8rem !important; margin: 0 !important; }
                .result-text { flex-grow: 1 !important; padding: 10px !important; text-align: center !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
                .interp-title { font-size: 1.1rem !important; font-weight: bold !important; text-transform: uppercase !important; }
                
                .footer { margin-top: 25px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 9px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                @media print { .sis-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>BECK SUICIDE INTENT SCALE (SIS) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Diagnosis</td>
                            <td>{d.diagnosis}</td>
                            <td className="label-bg">Assessed By</td>
                            <td>{d.clinician_name}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="sis-table">
                    <thead>
                        <tr>
                            <th style={{ width: '30px' }}>#</th>
                            <th style={{ width: '180px' }}>Scale Item</th>
                            <th>Patient Response / Observations</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sisItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td style={{ fontWeight: 'bold' }}>{item.title}</td>
                                <td>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* <div className="risk-banner">
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #000', paddingBottom: '10px' }}>
                        <div>
                            <small style={{ display: 'block' }}>CUMULATIVE SCORE</small>
                            <strong style={{ fontSize: '1.4rem' }}>Total: {totalScore}</strong>
                        </div>
                        <div className="risk-level" style={{ color: risk.color }}>{risk.text}</div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <strong>Clinical Recommendation:</strong> <span style={{ fontStyle: 'italic' }}>{risk.recommendation}</span>
                    </div>
                </div> */}

                <div style={{ marginTop: '15px' }}>
                    <strong>Additional Comments:</strong>
                    <div style={{ border: '1px solid #000', padding: '10px', minHeight: '40px', marginTop: '5px' }}>
                        {d.additional_comments || "No additional comments."}
                    </div>
                </div>

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Clinician Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}